import { ProductSlug, SkuOption } from "@/api/product-api";
import { useFormContext } from "react-hook-form";
import { RadioGroup, RadioGroupItem } from "@/v2-components/ui/radio-group";
import { cn } from "@/libs/utils";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from "@/v2-components/ui/form";
import { AspectRatio } from "@/v2-components/ui/aspect-ratio";
import { Button } from "@/v2-components/ui/button";
import PlusIcon from "@/v2-components/core/icon/plus-icon";
import MinusIcon from "@/v2-components/core/icon/minus-icon";
import Image from "next/image";
import { useCurrency } from "@/hooks/useCurrency";
import { useMount } from "react-use";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/v2-components/ui/accordion";
import { useTrans } from "@/utils/useTrans";
import { useProducts } from "@/services/product/useProductService";
import { useProductPrice } from "@/hooks/useProductPrice";
import { useLocale } from "@/store/locale-store";
import { ProductUtil } from "@/utils/ProductUtil";
import { CartUtil } from "@/utils/CartUtil";
import {
  ComponentId,
  ERROR_SECTION_WRAPPER,
  RAIL_ERROR_HIGHLIGHT,
} from "@/constants";
import { useBuyTactSuit } from "@/store/useBuyTactSuit";
import { Tooltip } from "@/v2-components/ui/tooltip";
import { getAvailableColors } from "@/utils/product-colors";
import { ColorSwatchContent } from "@/v2-components/cart-sheet/ColorSwatchContent";
import { NotifyMe } from "@/v2-components/pages/tactsuit/NotifyMe";
import { getNotifyMeOptions } from "@/utils/notify-me-options";
import { useId, useState } from "react";
import Dk3SizeGuideSheet from "@/components/tactsuit/tactglove-dk3/Dk3SizeGuideSheet";
import BulkOrderInquiryDialog from "@/v2-components/bulk-order-inquiry-dialog";
import { GLOVE_SIZES, hasEveryGloveSize } from "@/libs/product/glove-set";

// 사이즈 목록·세트 판정은 `libs/product/glove-set.ts` 한 곳 — `?bundle=` 진입 첫 선택과 SEO 가 같은 술어를 쓴다.
const SIZE_OPTIONS = GLOVE_SIZES;

const SIZE_LABELS: Record<string, string> = {
  small: "S",
  medium: "M",
  large: "L",
  "x-large": "XL",
};

export function TactGloveOptions({
  skuOptions,
  productSlug,
  // 상세 페이지(ProductOptionSelect)에서만 true. cart-sheet 경로는 기존 레이아웃 유지.
  showErrorWrapper = false,
  railLayout = false,
}: {
  skuOptions: SkuOption[];
  // 글러브 제품 slug (DK2/DK3). SKU·번들·재고를 이 slug 로 도출 — 하드코딩 금지.
  productSlug: ProductSlug;
  showErrorWrapper?: boolean;
  /**
   * 구매 레일(상세 페이지) 레이아웃. cart-sheet 는 기존 레이아웃을 유지해야 하므로
   * prop 으로 가른다.
   *
   * - **간격**: 섹션 간격을 부모의 `flex gap-12`(48px)로 통일한다(피그마 28715-139500).
   *   각 섹션이 자체 `mb` 를 들고 있으면 마지막 섹션 뒤에 죽은 여백이 남아 다음
   *   섹션까지 48 이 아니라 76px 로 벌어진다.
   * - **Size 섹션**(피그마 28715-139539): 헤더가 `Size` ←→ `Size guide` 링크 한 줄로
   *   단순해졌다. 기존의 큰 "Size Guide" 아코디언/버튼 박스는 사라지고 시트만 남는다.
   *   Notify 안내도 회색 박스 한 줄로 바뀐다.
   */
  railLayout?: boolean;
}) {
  /**
   * 사이즈/세트를 고르면 액세서리 섹션으로 스크롤한다.
   *
   * ⚠️ 두 라벨(사이즈 칩 · 전 사이즈 세트)에 **같은 8줄이 복사돼 있었다.**
   * `100`ms 와 `"accessories"` id 가 각각 두 번씩 적혀 있어 한쪽만 고치면 갈린다.
   *
   * 지연은 라디오 선택으로 목록이 다시 그려진 뒤에 스크롤하기 위한 것이다.
   * (레일 레이아웃에는 `id="accessories"` 블록이 없어 사실상 no-op 이다 — 시트 전용.)
   */
  const scrollToAccessories = () => {
    setTimeout(() => {
      document
        .getElementById("accessories")
        ?.scrollIntoView({ behavior: "smooth" });
    }, 100);
  };

  const { t } = useTrans(["gnb"]);
  const form = useFormContext();
  const showValidationErrors = useBuyTactSuit((s) => s.showValidationErrors);
  const currency = useCurrency();
  const products = useProducts();
  const { countryCode } = useLocale();
  const selectedColor = form.watch("options.color");
  // DK3 는 자체 구조화 사이즈 가이드 시트(Dk3SizeGuideSheet, 마케팅 페이지와 동일)를 사용.
  // DK2 는 기존 평면 이미지(tactglove-size-guide.png, "DK2" 박힘) accordion 유지.
  const isDk3 = productSlug === ProductSlug.TACTGLOVE_DK3;
  // 사이즈 칩 — 레일 64×64(피그마 28715:139546), cart-sheet 는 기존 60×60.
  const sizeChipClass = railLayout ? "h-16 w-16" : undefined;
  const [sizeGuideOpen, setSizeGuideOpen] = useState(false);
  const [bulkOrderOpen, setBulkOrderOpen] = useState(false);
  // 세트 구성 캡션 id — 세트 라디오가 `aria-describedby` 로 가리킨다. 이 컴포넌트는 PDP 레일과
  // 장바구니 시트에 각각 렌더될 수 있어 고정 id 면 겹친다.
  const setCompositionId = useId();

  const canSelectGloveSet = hasEveryGloveSize(
    skuOptions,
    productSlug,
    selectedColor,
  );

  const gloveSetProduct = products.data?.find(
    (p) => p.slug === CartUtil.getGloveBundleSlug(productSlug),
  );
  const gloveSetPrice = gloveSetProduct
    ? ProductUtil.getProductPrice({
        currency,
        countryCode,
        product: gloveSetProduct,
      })
    : null;

  const colorOptions = getAvailableColors(skuOptions);

  // 사이즈 variant 제품이면서 품절 SKU 가 하나라도 있으면 "Not available in your size?" 노출
  const gloveProduct = products.data?.find((p) => p.slug === productSlug);
  const notifyMeConfig = getNotifyMeOptions(gloveProduct);
  const hasAnySoldOutSku = (gloveProduct?.skuOptions ?? []).some(
    (opt) => (opt.stock ?? 0) <= 0,
  );
  const showNotifyMeLink = notifyMeConfig.hasSizeVariant && hasAnySoldOutSku;
  const selectedSize = form.watch("options.size");

  useMount(() => {
    const availableColors = colorOptions
      .filter((c) => c.available)
      .map((c) => c.color);

    if (availableColors.length > 0) {
      form.setValue(`options.color`, availableColors[0], {
        shouldValidate: true,
      });
    }
  });

  const { displayDicountPercent } = useProductPrice(gloveSetPrice);

  if (!gloveSetPrice) {
    return null;
  }

  return (
    <>
      {/* Color — 색상 1개면 useMount 가 자동 선택, 피커는 숨김 (DK3 단일색상) */}
      {colorOptions.length > 1 && (
        <FormField
          name="options.color"
          control={form.control}
          render={({ field }) => (
            <FormItem
              className={cn(
                railLayout ? "space-y-3" : "mb-6 space-y-3 laptop:mb-7",
                // ⚠️ 아래 Size 섹션·ColorOptions 와 **같은 규칙**이어야 한다.
                // 여기만 `!railLayout` 가드가 빠져 있어 레일에서도 시트용
                // `ERROR_SECTION_WRAPPER`(-mx-4·py-4)가 걸렸다 — 피그마 간격을
                // 16px 밀고 레일 박스(496) 밖으로 풀블리드된다.
                // 지금은 DK3 가 단색이라 이 분기가 안 그려져 드러나지 않지만,
                // 다색 글러브가 나오면 이 섹션만 시트 기하로 렌더된다.
                showErrorWrapper && !railLayout && ERROR_SECTION_WRAPPER,
                showErrorWrapper &&
                  showValidationErrors &&
                  !field.value &&
                  (railLayout ? RAIL_ERROR_HIGHLIGHT : "bg-red-10"),
              )}
              id={ComponentId.BUY_TACTSUIT_COLOR_OPTION}
            >
              <div className="flex flex-col gap-y-2">
                <h3 className="text-2xl capitalize">
                  {t("Select Color", { defaultValue: "Select Color" })}
                </h3>
                {field.value && (
                  <h6 className="text-sm capitalize text-blue-60">
                    {field.value}
                  </h6>
                )}
                {!field.value && (
                  <h6 className="text-sm capitalize text-red-60">
                    {t("Please select a color")}
                  </h6>
                )}
              </div>
              <FormControl>
                <RadioGroup
                  onValueChange={(value) => {
                    field.onChange(value);
                    form.setValue("options.size", "");
                  }}
                  value={field.value}
                  className="flex items-center"
                >
                  {colorOptions.map(({ color, name, available }) => {
                    return (
                      <FormItem
                        key={color}
                        className="group flex items-center space-y-0"
                      >
                        <FormControl>
                          <RadioGroupItem
                            value={color}
                            className="peer sr-only"
                            disabled={!available}
                          />
                        </FormControl>
                        <FormLabel className="flex cursor-pointer flex-col items-center gap-1">
                          {available ? (
                            <ColorChip color={color} />
                          ) : (
                            <Tooltip
                              variant="light"
                              tooltipContent={<p>{t("Sold Out")}</p>}
                            >
                              <div>
                                <ColorChip color={color} disabled />
                              </div>
                            </Tooltip>
                          )}
                        </FormLabel>
                      </FormItem>
                    );
                  })}
                </RadioGroup>
              </FormControl>
            </FormItem>
          )}
        />
      )}

      {/* Size */}
      <FormField
        name="options.size"
        control={form.control}
        render={({ field }) => (
          <FormItem
            className={railLayout ? undefined : "mb-6 laptop:mb-7"}
            id={ComponentId.BUY_TACTSUIT_SIZE_OPTION}
          >
            {/* ⚠️ 에러 배경(핑크)은 **섹션 전체**를 감싼다 — 피그마 27471:53956 에서
                제목·경고문까지 핑크 안이고, Size 섹션(28715:139539)의 구성은
                Header + Contents(칩 · 세트 행 · Notice) 전부다.
                헤더나 픽커 한쪽에만 걸면 나머지가 밖에 남고, 픽커에만 걸면 핑크가
                아래 회색 Notice 를 4px 덮는다(핑크 16 > 블록 간격 12) — 실측. */}
            <div
              className={cn(
                railLayout &&
                  showErrorWrapper &&
                  showValidationErrors &&
                  !field.value &&
                  RAIL_ERROR_HIGHLIGHT,
              )}
            >
              {/* 헤더 — 레일: `Size` ←→ `Size guide` 링크 한 줄 (피그마 28715:139540).
                기존 "Select Size" + 선택값 표기는 cart-sheet 경로에만 남는다. */}
              {railLayout && (
                <div className="flex flex-col gap-2">
                  <div className="flex items-center justify-between">
                    {/* `gnb.json` 의 `Size` 는 전역 키다. 한국어를 '크기' → '사이즈'
                        로 바꿨는데(의류 사이즈 문맥), 다른 사용처는 TactVisor 사이즈
                        가이드 표 헤더 한 곳뿐이고 거기서도 '사이즈'가 자연스럽다. */}
                    <h3 className="text-2xl">{t("Size")}</h3>
                    {/* 시트가 있는 DK3 만 헤더 링크. DK2 는 평면 이미지 아코디언을
                      아래에 그대로 두므로 여기서 중복 노출하지 않는다. */}
                    {isDk3 && (
                      <button
                        type="button"
                        onClick={() => setSizeGuideOpen(true)}
                        className="text-base font-500 tracking-[0.1px] text-blue-60 underline"
                      >
                        {t("Size Guide")}
                      </button>
                    )}
                  </div>
                  {!field.value && (
                    // 보조줄 토큰 = Label/Medium 12/16 w500 tracking .5 red-60
                    // (피그마 28715:139571 "Please select an option" 과 동일)
                    <p className="text-sm font-500 tracking-[0.5px] text-red-60">
                      {t("Please select a size")}
                    </p>
                  )}
                </div>
              )}
              {/* 에러 핑크(bg-red-10)·풀블리드 래퍼는 사이즈 픽커에만 한정.
                Size Guide·Notify·Bulk(Figma상 별도 섹션)까지 번지면 안 됨. */}
              <div
                className={cn(
                  railLayout ? "mt-5" : "space-y-5",
                  !railLayout && showErrorWrapper && ERROR_SECTION_WRAPPER,
                  !railLayout &&
                    showErrorWrapper &&
                    showValidationErrors &&
                    !field.value &&
                    "bg-red-10",
                )}
              >
                {!railLayout && (
                  <div className="flex flex-col gap-y-2">
                    <h3 className="text-2xl capitalize">
                      {t("Select Size", { defaultValue: "Select Size" })}
                    </h3>
                    {field.value && (
                      <h6 className="text-sm capitalize text-blue-60">
                        {field.value}
                      </h6>
                    )}
                    {!field.value && (
                      <h6 className="text-sm text-red-60">
                        {t("Please select a size")}
                      </h6>
                    )}
                  </div>
                )}
                <FormControl>
                  <RadioGroup
                    onValueChange={(e) => {
                      field.onChange(e);
                      // 사이즈가 바뀌면 이전 사이즈의 액세서리(이너글러브 S 등)가
                      // 목록에서 사라지므로 선택도 함께 해제한다. 안 하면 화면에
                      // 없는 물건이 그대로 결제된다.
                      //
                      // ⚠️ **cart-sheet 경로 전용이다.** 레일(PDP)은 액세서리를
                      // 이 폼이 아니라 zustand(`useBuyTactSuit`)에 담으므로 이
                      // setValue 는 아무 필드도 건드리지 않는다 — 레일의 같은
                      // 방어는 `[slug].tsx` 의 `dropUnofferedAccessories` 가 한다.
                      // 여기만 보고 "양쪽 다 막혀 있다"고 판단하지 말 것.
                      form.setValue("additionalProducts", []);
                    }}
                    value={field.value}
                    className="flex flex-col items-start justify-start gap-3"
                  >
                    <div
                      className={cn(
                        // 세트 칩이 사이즈 칩 옆에 붙는다(#6028) — 좁은 화면에서는 같은 그룹 안에서
                        // 다음 줄로 넘어간다(잘리지 않게).
                        "flex flex-wrap items-center",
                        // 칩 간격 — 레일 12px(피그마), cart-sheet 는 기존 16px
                        railLayout ? "gap-3" : "gap-4",
                      )}
                    >
                      {SIZE_OPTIONS.map((size) => {
                        const sku = `${productSlug}-${selectedColor}-${size}`;
                        const skuOption = skuOptions.find((o) => o.sku === sku);
                        const stock = skuOption?.stock ?? 0;
                        return (
                          <FormItem
                            key={size}
                            className="group flex items-center"
                          >
                            <FormControl>
                              <RadioGroupItem
                                value={size}
                                className="sr-only"
                                disabled={stock <= 0}
                              />
                            </FormControl>
                            <FormLabel
                              className="cursor-pointer ring-blue-60 ring-offset-2 peer-focus-visible:ring-2"
                              onClick={scrollToAccessories}
                            >
                              {stock <= 0 ? (
                                <Tooltip
                                  variant="light"
                                  tooltipContent={<p>{t("Sold Out")}</p>}
                                >
                                  <div>
                                    {/* 이 분기 자체가 `stock <= 0` 이라 조건을 다시 걸지 않는다. */}
                                    <Size
                                      className={cn(
                                        sizeChipClass,
                                        "bg-opacity-20",
                                      )}
                                    >
                                      <span className="text-lg font-500 tracking-[0.15px]">
                                        {SIZE_LABELS[size] ??
                                          size.toUpperCase()}
                                      </span>
                                      <div className="absolute inset-0 z-10 flex size-full items-center justify-center text-coolgray-20">
                                        <ThinSlashSVG />
                                      </div>
                                    </Size>
                                  </div>
                                </Tooltip>
                              ) : (
                                <Size className={sizeChipClass}>
                                  <span className="text-lg font-500 tracking-[0.15px]">
                                    {SIZE_LABELS[size] ?? size.toUpperCase()}
                                  </span>
                                </Size>
                              )}
                            </FormLabel>
                          </FormItem>
                        );
                      })}

                      {/* S·M·L·XL 세트 — 사이즈 칩과 **같은 위계의 칩**(#6028). 종전(#5402)엔 칩 줄 아래
                          전폭 행에 세트 판매가·정가 줄긋기·할인 배지를 그렸는데, 단품 사이즈를 고르는
                          자리에 큰 세트 가격이 함께 보여 부담스럽다는 요청으로 **구성 + 할인율만** 남긴다.
                          가격은 세트를 고르면 상단 대표 가격(`TactSuitCard.PriceDetail`)이 세트 기준으로
                          바뀌어 보여 준다 — 담기·결제도 그 세트 SKU(`all-size`) 기준이라 값은 그대로다.
                          높이·외곽선·선택 표시는 `Size` 칩을 그대로 쓰고, 폭만 문구에 맞춰 넓힌다.
                          라벨은 `All-Size Set` — 옆 칩이 사이즈를 이미 보여 줘 나열하지 않는다
                          (`__tests__/v2-components/dk3-set-label.test.ts`). */}
                      <FormItem className="group flex items-center">
                        <FormControl>
                          <RadioGroupItem
                            value={"all-size"}
                            aria-describedby={setCompositionId}
                            className="sr-only"
                            disabled={!canSelectGloveSet}
                          />
                        </FormControl>
                        <FormLabel
                          className="cursor-pointer ring-blue-60 ring-offset-2 peer-focus-visible:ring-2"
                          onClick={scrollToAccessories}
                        >
                          {(() => {
                            // 품절 표현은 옆 품절 사이즈 칩과 같게 — 흐린 배경 + 사선 + "Sold Out" 툴팁.
                            // 세트는 네 사이즈 중 하나만 품절이어도 비활성이라 둘이 같은 줄에 나란히
                            // 보이는 일이 잦다. 라벨 글자색도 사이즈 칩처럼 그대로 두고, 할인율만 회색.
                            const chip = (
                              <Size
                                className={cn(
                                  sizeChipClass,
                                  "w-auto flex-col gap-0.5 px-3 text-center",
                                  !canSelectGloveSet && "bg-opacity-20",
                                )}
                              >
                                <span className="text-lg font-500 leading-tight tracking-[0.15px]">
                                  {t("All-Size Set", { ns: "gnb" })}
                                </span>
                                {Number.isFinite(displayDicountPercent) &&
                                  displayDicountPercent > 0 && (
                                    <span className="text-xs font-600 leading-tight text-red-60 group-has-[:disabled]:text-coolgray-30">
                                      {t("{{percent}}% off", {
                                        ns: "gnb",
                                        percent: displayDicountPercent,
                                      })}
                                    </span>
                                  )}
                                {!canSelectGloveSet && (
                                  // `ThinSlashSVG` 는 68×68 고정이라 문구 폭만큼 넓은 이 칩에선
                                  // 모서리까지 닿지 않는다 — 칩 크기로 늘리고 선 두께는 유지한다.
                                  <svg
                                    className="absolute inset-0 z-10 size-full text-coolgray-20"
                                    viewBox="0 0 100 100"
                                    preserveAspectRatio="none"
                                    fill="none"
                                    aria-hidden
                                  >
                                    <line
                                      x1="0"
                                      y1="100"
                                      x2="100"
                                      y2="0"
                                      stroke="currentColor"
                                      vectorEffect="non-scaling-stroke"
                                    />
                                  </svg>
                                )}
                              </Size>
                            );
                            return canSelectGloveSet ? (
                              chip
                            ) : (
                              <Tooltip
                                variant="light"
                                tooltipContent={<p>{t("Sold Out")}</p>}
                              >
                                <div>{chip}</div>
                              </Tooltip>
                            );
                          })()}
                        </FormLabel>
                      </FormItem>
                    </div>
                    {/* 세트 구성 — 칩 라벨은 `All-Size Set` 이라 무엇이 들었는지는 여기서 말한다.
                        고르기 전에 알아야 판단할 수 있어 선택과 무관하게 항상 보인다. */}
                    <p
                      id={setCompositionId}
                      className="text-sm tracking-[0.25px] text-coolgray-60"
                    >
                      {t("All-Size Set includes S, M, L and XL (1 pair each)", {
                        ns: "gnb",
                      })}
                    </p>
                  </RadioGroup>
                </FormControl>
              </div>

              {/* Size Guide·Notify·Bulk — 섹션 구성원이라 에러 핑크 안에 들어간다.
                데스크탑 px-4 로 사이즈 픽커 콘텐츠(핑크 박스 laptop:p-4 인셋)와 좌측 정렬 일치.
                레일에서는 간격이 피그마 수치로 고정돼 있어 그 px-4·mt-2 를 쓰지 않는다. */}
              <div
                className={
                  railLayout
                    ? "mt-3 flex flex-col gap-3"
                    : "mt-2 space-y-3 laptop:px-4"
                }
              >
                {isDk3 ? (
                  <>
                    {/* DK3: 마케팅 페이지와 동일한 구조화 사이즈 가이드 시트 재사용.
                      레일에서는 트리거가 헤더의 "Size guide" 링크로 옮겨갔으므로
                      큰 버튼 박스는 그리지 않고 시트만 마운트한다. */}
                    {!railLayout && (
                      <Button
                        type="button"
                        variant="darkgray-text"
                        onClick={() => setSizeGuideOpen(true)}
                        className="w-full justify-between rounded border border-coolgray-20 bg-white px-4 py-3 text-base font-400 hover:bg-white"
                      >
                        <span>{t(`Size Guide`)}</span>
                        <PlusIcon />
                      </Button>
                    )}
                    <Dk3SizeGuideSheet
                      open={sizeGuideOpen}
                      onOpenChange={setSizeGuideOpen}
                    />
                  </>
                ) : (
                  <Accordion type="single" collapsible>
                    <AccordionItem
                      value="item-1"
                      className="rounded border border-coolgray-20 bg-white"
                    >
                      <AccordionTrigger asChild>
                        <Button
                          variant="darkgray-text"
                          className="group w-full justify-between px-4 py-3 text-base font-400 hover:bg-white"
                        >
                          <span>{t(`Size Guide`)}</span>
                          <PlusIcon className="block group-data-[state=open]:hidden" />
                          <MinusIcon className="hidden group-data-[state=open]:block" />
                        </Button>
                      </AccordionTrigger>
                      <AccordionContent className="p-0 pb-3">
                        <AspectRatio>
                          <Image
                            src={`/images/size-guide/tactglove-size-guide.png`}
                            alt={`tactglove size guide`}
                            fill
                          />
                        </AspectRatio>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                )}

                {showNotifyMeLink &&
                  (railLayout ? (
                    /* 레일: 회색 박스 한 줄 (피그마 28715:139562) */
                    <div className="flex items-center justify-between rounded-lg bg-coolgray-10 p-5">
                      <p className="text-base tracking-[0.25px] text-coolgray-90">
                        {t("Not available in your size?")}
                      </p>
                      <NotifyMe
                        productSlug={productSlug}
                        initialColor={selectedColor}
                        initialSize={selectedSize}
                        trigger={
                          <button
                            type="button"
                            className="text-base font-500 tracking-[0.1px] text-blue-60 underline"
                          >
                            {t("Notify me")}
                          </button>
                        }
                      />
                    </div>
                  ) : (
                    <div className="!mt-2 flex flex-col gap-2 py-4">
                      <h4 className="text-2xl">
                        {t("Not available in your size?")}
                      </h4>
                      <NotifyMe
                        productSlug={productSlug}
                        initialColor={selectedColor}
                        initialSize={selectedSize}
                        trigger={
                          <button
                            type="button"
                            className="self-start text-sm font-600 text-blue-60 underline"
                          >
                            {t("Notify me")}
                          </button>
                        }
                      />
                    </div>
                  ))}
                {/* 대량구매 문의 — 새 피그마 Size 섹션(28715:139539)에는 **없지만
                  유지하기로 결정**했다(2026-08-06). DK3 전용 리드 채널이고, 없애도
                  대체 경로가 없어서다. 표시는 위 Notify 와 같은 회색 박스 한 줄로
                  맞춰 뒀다.
                  ⚠️ 피그마와 대조하다 "없는 항목" 으로 보고 지우지 말 것.
                  노출 조건은 `isDk3` — 글러브 전체가 아니라 **DK3 한 곳뿐**이다
                  (DK2 는 PDP 가 /tactsuit/ 로 리다이렉트돼 도달 불가). */}
                {isDk3 && (
                  <div
                    className={cn(
                      railLayout
                        ? "flex items-center justify-between rounded-lg bg-coolgray-10 p-5"
                        : "!mt-2 flex flex-col gap-2 py-4",
                    )}
                  >
                    <h4
                      className={
                        railLayout
                          ? "text-base tracking-[0.25px] text-coolgray-90"
                          : "text-2xl"
                      }
                    >
                      {t("Buying 5+ units?")}
                    </h4>
                    <button
                      type="button"
                      onClick={() => setBulkOrderOpen(true)}
                      className={cn(
                        "text-blue-60 underline",
                        railLayout
                          ? "text-base font-500 tracking-[0.1px]"
                          : "self-start text-sm font-600",
                      )}
                    >
                      {t("Contact us for a bulk order")}
                    </button>
                    {bulkOrderOpen && (
                      <BulkOrderInquiryDialog
                        open
                        onOpenChange={setBulkOrderOpen}
                        desiredProduct={gloveProduct?.name}
                      />
                    )}
                  </div>
                )}
              </div>
            </div>
          </FormItem>
        )}
      />
    </>
  );
}

const ColorChip = ({
  color,
  disabled = false,
}: {
  color: string;
  disabled?: boolean;
}) => {
  return (
    <div className={cn("px-2 py-1.5")}>
      <div className="relative h-6 w-6">
        <div
          className={cn(
            "relative size-full overflow-hidden rounded-full",
            "group-focus-within::outline-blue-60 group-focus-within::outline outline-2 outline-offset-[6px] group-has-[:checked]:outline group-has-[:checked]:outline-blue-60",
            { "opacity-20": disabled },
          )}
        >
          <ColorSwatchContent color={color} />
        </div>
        {disabled && (
          <div className="absolute inset-0 z-10 flex size-full items-center justify-center text-white">
            <SlashSVG />
          </div>
        )}
      </div>
    </div>
  );
};

const SlashSVG = () => {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 18 18"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M0.353516 17.3242L17.3241 0.353656" stroke="currentColor" />
    </svg>
  );
};

export const ThinSlashSVG = () => {
  return (
    <svg
      width="68"
      height="68"
      viewBox="0 0 68 68"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M0.353516 67.3535L67.3535 0.353515" stroke="currentColor" />
    </svg>
  );
};

export const Size = ({
  className,
  children,
}: React.PropsWithChildren<{ className?: string }>) => {
  return (
    <div
      className={cn(
        "relative flex h-[60px] w-[60px] items-center justify-center rounded border border-coolgray-20 bg-white",
        "transition-colors enabled:hover:border-coolgray-90",
        "group-has-[:checked]:outline group-has-[:checked]:outline-2 group-has-[:checked]:-outline-offset-2 group-has-[:checked]:outline-blue-60",
        className,
      )}
    >
      {children}
    </div>
  );
};
